import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type PromotionCodesListOutput = OutputRouter['promotionCodes']['list']
export type PromotionCodeItem = PromotionCodesListOutput['items'][number]
export type PromotionCodeDetails = OutputRouter['promotionCodes']['create']
export type ValidatePromotionCodeOutput = OutputRouter['promotionCodes']['validate']
export type CreatePromotionCodeOutput = OutputRouter['promotionCodes']['create']
export type UpdatePromotionCodeOutput = OutputRouter['promotionCodes']['update']
export type DeletePromotionCodesOutput = OutputRouter['promotionCodes']['delete']

// Input types
export type ListPromotionCodesInput = InputRouter['promotionCodes']['list']
export type CreatePromotionCodeInput = InputRouter['promotionCodes']['create']
export type UpdatePromotionCodeInput = InputRouter['promotionCodes']['update']
export type DeletePromotionCodesInput = InputRouter['promotionCodes']['delete']
export type ValidatePromotionCodeInput = InputRouter['promotionCodes']['validate']

// Query hooks
export const usePromotionCodesList = (params: ListPromotionCodesInput, enabled = true) => {
    return trpc.promotionCodes.list.useQuery(params, {
        enabled: enabled && (!!params.storeId || !!params.promotionId),
    })
}

export const usePromotionCodeById = (id: string, enabled = true) => {
    return trpc.promotionCodes.getById.useQuery(
        { id },
        {
            enabled: enabled && !!id,
            retry: 2,
        },
    )
}

export const useValidatePromotionCode = (
    params: ValidatePromotionCodeInput,
    enabled = true,
) => {
    return trpc.promotionCodes.validate.useQuery(params, {
        enabled: enabled && !!params.code && !!params.storeId,
        retry: 1,
    })
}

// Mutation hooks
export const useCreatePromotionCode = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotionCodes.create.useMutation({
        onSuccess: () => {
            utils.promotionCodes.list.invalidate()
            utils.promotions.getById.invalidate()
            utils.promotions.list.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion code created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create promotion code',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdatePromotionCode = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotionCodes.update.useMutation({
        onSuccess: () => {
            utils.promotionCodes.list.invalidate()
            utils.promotionCodes.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion code updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update promotion code',
                variant: 'destructive',
            })
        },
    })
}

export const useDeletePromotionCodes = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.promotionCodes.delete.useMutation({
        onSuccess: () => {
            utils.promotionCodes.list.invalidate()
            utils.promotions.getById.invalidate()
            utils.promotions.list.invalidate()
            toast({
                title: 'Success',
                description: 'Promotion code(s) deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete promotion code(s)',
                variant: 'destructive',
            })
        },
    })
}

